// ============================================================================
// updater.js — automatikus verziófrissítés a felhasználó beavatkozása nélkül.
//
// A PROBLÉMA, amit megold:
//
// Egy PWA service workere agresszíven gyorsítótáraz. Új verzió telepítése
// után a böngésző a háttérben letölti az újat, de "várakozó" állapotban
// hagyja, amíg a felhasználó BE NEM ZÁRJA az alkalmazás ÖSSZES lapját.
// Egy folyamatosan nyitva tartott csevegőablaknál ez akár napokig is
// eltarthat - addig a régi kód fut, régi hibákkal.
//
// HÁROM RÉTEGŰ MEGOLDÁS:
//
//  1. A service worker `skipWaiting()`-gel azonnal átveszi az irányítást
//     (ez a service-worker.js-ben van).
//  2. A kliens figyeli a `controllerchange` eseményt: amint az új worker
//     átvette az irányítást, újratölti az oldalt.
//  3. Aktív verzióellenőrzés: a nem gyorsítótárazott `version.json`
//     lekérésével akkor is észrevesszük a frissítést, ha a service worker
//     valamiért nem jelezne (pl. le van tiltva, vagy a böngésző nem
//     támogatja).
// ============================================================================

import { log } from "./log.js";

// A jelenleg futó kód verziója.
//
// 2026-10-06: a build a csomagba FORDÍTJA (__BUILD_VERSION__, lásd build.mjs). Korábban
// a kliens induláskor a SZERVEREN lévő version.json-t jegyezte meg "futó" verziónak -
// ha az oldalt egy régi gyorsítótárból (service worker) szolgálták ki, a szerver már az
// újat mondta, a kliens azt tekintette futónak, és SOHA nem vette észre, hogy a kódja
// elavult. Most a saját, beépített verzióját veti össze a szerverével.
let runningVersion = null;

/** A csomagba fordított build-verzió, vagy null (fejlesztői/tesztfuttatás, ahol nincs csomag). */
export function beepitettVerzio() {
  return typeof __BUILD_VERSION__ === "string" ? __BUILD_VERSION__ : null;
}

/** A csomagba fordított kiadás-jelzés ("V94"), vagy null (nincs release.json / fejlesztői futtatás). Csak megjelenítésre. */
export function beepitettKiadas() {
  const r = typeof __BUILD_RELEASE__ === "number" ? __BUILD_RELEASE__ : null;
  return Number.isInteger(r) && r > 0 ? `V${r}` : null;
}

// Hányszor ellenőrizzünk: fókuszba kerüléskor és 15 percenként.
const PERIODIC_CHECK_MS = 15 * 60 * 1000;

let reloadInProgress = false;
let onUpdateAvailable = null;

/**
 * Az oldal újratöltése úgy, hogy a régi gyorsítótár biztosan eltűnjön.
 * A `reloadInProgress` őr megakadályozza a végtelen újratöltési hurkot,
 * ami akkor keletkezne, ha az új verzió is frissítést észlelne.
 */
async function forceReload(reason) {
  if (reloadInProgress) return;
  reloadInProgress = true;

  log.debug("[updater] újratöltés:", reason);

  // A Cache Storage teljes ürítése: enélkül a service worker a régi
  // fájlokat szolgálná ki az újratöltés után is.
  try {
    if (window.caches) {
      const names = await window.caches.keys();
      await Promise.all(names.map((n) => window.caches.delete(n)));
    }
  } catch (err) {
    // Nem kritikus: a verziózott URL-ek miatt a friss fájl így is megjön.
    log.error("[updater] a cache ürítése nem sikerült:", err);
  }

  // `true` paraméter: a böngésző a szerverről töltsön, ne a cache-ből.
  window.location.reload();
}

/**
 * A szerveren lévő verzió lekérése. Szándékosan `cache: "no-store"`,
 * hogy a böngésző SOHA ne a saját másolatát adja vissza - enélkül épp
 * az a fájl ragadna be, aminek a frissülést kellene jeleznie.
 */
// Exportálva: a devCache.js is ezt használja (ugyanaz a lekérdezés, ugyanaz
// a cache-elkerülés) - nem külön, párhuzamos logikával dönti el, mi a
// jelenlegi verzió.
export async function fetchServerVersion() {
  try {
    const res = await fetch(`./version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return data && typeof data.version === "string" ? data.version : null;
  } catch (err) {
    // Offline állapotban ez normális - nem hiba, csak most nem tudjuk
    // ellenőrizni.
    return null;
  }
}

/** Egyszeri verzióellenőrzés; ha eltérés van, frissít. */
async function checkVersion() {
  const serverVersion = await fetchServerVersion();
  if (!serverVersion) return;

  if (runningVersion === null) {
    // Első ellenőrzés: megjegyezzük, mivel indultunk.
    runningVersion = serverVersion;
    log.debug("[updater] futó verzió:", runningVersion);
    return;
  }

  if (serverVersion !== runningVersion) {
    log.debug(`[updater] új verzió elérhető: ${runningVersion} → ${serverVersion}`);
    if (onUpdateAvailable) {
      onUpdateAvailable(serverVersion);
    } else {
      forceReload("verzióeltérés");
    }
  }
}

/**
 * A frissítéskezelés elindítása.
 *
 * @param {function} [notifyCallback] Ha megadod, ez fut le új verzió
 *   észlelésekor az azonnali újratöltés HELYETT - így felajánlhatod a
 *   frissítést a felhasználónak ahelyett, hogy elvennéd alóla a képernyőt
 *   gépelés közben.
 */
export function startUpdateWatcher(notifyCallback) {
  onUpdateAvailable = notifyCallback || null;
  // A futó verzió a saját, beépített verzió (ha van) - nem az, amit a szerver mond.
  if (runningVersion === null) runningVersion = beepitettVerzio();

  // --- 1. réteg: a service worker átvételének figyelése
  if ("serviceWorker" in navigator) {
    // Amint egy ÚJ service worker átveszi az irányítást, a futó kód
    // elavult - újratöltés.
    // Az ELSŐ látogatáskor az oldalnak még nincs service workere; amikor a
    // frissen telepített worker átveszi (clients.claim), szintén
    // "controllerchange" esemény jön - de ez nem új verzió. AUDITBÓL: korábban
    // ez is frissítésnek számított, és az első látogatás egy fölösleges
    // újratöltéssel járt (a nyelvválasztás vagy a belépés közepén is).
    let voltVezerlo = Boolean(navigator.serviceWorker.controller);
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!voltVezerlo) {
        voltVezerlo = true;
        return;
      }
      // Ha a hívó megadott értesítő visszahívást, AZ dönt az újratöltés
      // időpontjáról. FONTOS, VALÓS HIBÁBÓL TANULVA: korábban ez az ág a
      // visszahívást megkerülve azonnal újratöltött, így egy kiadás a
      // gépelés közepén, félig feltöltött képpel is elránthatta az oldalt.
      if (onUpdateAvailable) {
        onUpdateAvailable(null);
        return;
      }
      forceReload("a service worker átvette az irányítást");
    });

    // Frissítés keresése induláskor és fókuszba kerüléskor.
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.update().catch(() => {});

        // Ha a böngésző új workert talál, megvárjuk, míg települ, és
        // azonnal aktiváljuk - nem várunk a lapok bezárására.
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              log.debug("[updater] új service worker telepítve");
              // A skipWaiting() a worker oldalán már megtörtént; ha mégis
              // várakozna, itt kérjük meg rá.
              if (registration.waiting) {
                registration.waiting.postMessage({ type: "SKIP_WAITING" });
              }
            }
          });
        });
      })
      .catch(() => {});
  }

  // --- 2. réteg: aktív verzióellenőrzés
  checkVersion();

  // Fókuszba kerüléskor (a felhasználó visszatér a laphoz vagy az
  // alkalmazáshoz) - ez a leggyakoribb pillanat, amikor a frissítés
  // észrevétlenül elvégezhető.
  window.addEventListener("focus", checkVersion);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) checkVersion();
  });

  // Hosszan nyitva tartott ablakhoz: időszakos ellenőrzés.
  setInterval(checkVersion, PERIODIC_CHECK_MS);
}

/** Kézi frissítés kiváltása (pl. egy "Frissítés" gombról). */
export function applyUpdate() {
  forceReload("kézi frissítés");
}
